import { Controller } from "@hotwired/stimulus"
import type { HLJSApi } from "highlight.js"

// highlight.js (core + languages) is ~100KB. Load it lazily on connect so it
// stays out of the global application bundle that every page pays for. The
// import + registration runs once per page and is cached on a module-level
// promise shared across controller instances.
let hljsPromise: Promise<HLJSApi> | null = null

function loadHljs(): Promise<HLJSApi> {
  if (hljsPromise) return hljsPromise

  hljsPromise = (async () => {
    const [
      { default: hljs },
      { default: bash },
      { default: css },
      { default: javascript },
      { default: json },
      { default: python },
      { default: ruby },
      { default: shell },
      { default: typescript },
      { default: xml },
      { default: yaml },
    ] = await Promise.all([
      import("highlight.js/lib/core"),
      import("highlight.js/lib/languages/bash"),
      import("highlight.js/lib/languages/css"),
      import("highlight.js/lib/languages/javascript"),
      import("highlight.js/lib/languages/json"),
      import("highlight.js/lib/languages/python"),
      import("highlight.js/lib/languages/ruby"),
      import("highlight.js/lib/languages/shell"),
      import("highlight.js/lib/languages/typescript"),
      import("highlight.js/lib/languages/xml"),
      import("highlight.js/lib/languages/yaml"),
    ])

    hljs.registerLanguage("bash", bash)
    hljs.registerLanguage("css", css)
    hljs.registerLanguage("javascript", javascript)
    hljs.registerLanguage("json", json)
    hljs.registerLanguage("python", python)
    hljs.registerLanguage("ruby", ruby)
    hljs.registerLanguage("shell", shell)
    hljs.registerLanguage("typescript", typescript)
    hljs.registerLanguage("html", xml)
    hljs.registerLanguage("xml", xml)
    hljs.registerLanguage("yaml", yaml)

    return hljs
  })()

  return hljsPromise
}

// Applies syntax highlighting to code blocks in rendered Action Text content.
// Handles three cases:
// 1. Lexxy code blocks: <pre data-language="ruby">…<br>…</pre>
// 2. Plain <pre> without language or nested <code> (e.g. pasted HTML)
// 3. Standard <pre><code> (e.g. from markdown)
// All are normalised to <pre><code>…</code></pre> for highlight.js + github-dark theme.
export default class extends Controller {
  async connect() {
    const pres = this.element.querySelectorAll("pre")
    if (pres.length === 0) return

    const hljs = await loadHljs()

    pres.forEach((pre) => {
      let code = pre.querySelector("code")

      if (code && code.classList.contains("hljs")) return // already highlighted

      if (!code) {
        // No <code> wrapper — create one. Lexxy uses <br> for newlines.
        const lang = pre.getAttribute("data-language") || ""
        const raw = pre.innerHTML.replace(/<br\s*\/?>/gi, "\n")
        const text = new DOMParser().parseFromString(raw, "text/html").body.textContent || ""

        code = document.createElement("code")
        if (lang) code.className = `language-${lang}`
        code.textContent = text
        pre.textContent = ""
        pre.appendChild(code)
      }

      hljs.highlightElement(code)
    })
  }
}
